import { Fragment, type ReactNode, useCallback, useEffect, useState } from 'react';
import { Badge, type Notify, Panel, type Tone, btn, btnGhost, input } from '../components/ui.js';
import { fmtWhen } from '../lib/format.js';
import {
  type LeadFacets,
  type LeadStatus,
  type MktCampaign,
  type MktLead,
  type MktLeadDetail,
  createCampaign,
  createLead,
  getLead,
  getLeadFacets,
  getMarketingSettings,
  inviteLead,
  listCampaigns,
  listLeads,
  testSampleDraft,
  testSend,
  updateMarketingSettings,
} from '../lib/internal.js';
import { CampaignManager } from './CampaignManager.js';

const STATUS_TONE: Record<string, Tone> = {
  queued: 'neutral',
  drafted: 'amber',
  sent: 'sky',
  replied: 'emerald',
  bounced: 'rose',
  unsubscribed: 'rose',
  active: 'emerald',
  paused: 'amber',
  draft: 'neutral',
};

/**
 * Cold-outreach marketing — STRICTLY INTERNAL (admin-only; never on the SDK or
 * the customer app). Drive campaigns over the marketing D1: create, enroll leads,
 * and work an inbox of AI-drafted messages (approve + send, human-in-the-loop,
 * with the per-campaign daily cap enforced server-side).
 */
export function Marketing({ notify }: { notify: Notify }) {
  const [campaigns, setCampaigns] = useState<MktCampaign[]>([]);
  const [selected, setSelected] = useState<MktCampaign | null>(null);
  // Which action is in flight (drives per-button spinners); null = idle.
  const [action, setAction] = useState<string | null>(null);
  const busy = action !== null;

  const [view, setView] = useState<'campaigns' | 'leads' | 'test'>('campaigns');
  // Full-screen campaign management overlay.
  const [manageOpen, setManageOpen] = useState(false);
  // Editable global ceiling on cold emails/day (per campaign). String while editing.
  const [maxDaily, setMaxDaily] = useState('');

  const loadSettings = useCallback(async () => {
    try {
      const s = await getMarketingSettings();
      setMaxDaily(String(s.maxDailyCap));
      setDailyCap(String(s.maxDailyCap)); // new campaigns default to the account max
    } catch {
      /* non-fatal — the field just stays blank until retried */
    }
  }, []);

  // Seed/test-send form (inbox-placement testing — mail-tester.com, etc.).
  const [testTo, setTestTo] = useState('');
  const [testSubject, setTestSubject] = useState('');
  const [testBody, setTestBody] = useState('');
  // Which campaign's brief to draft the "load a sample" content from ('' = newest).
  const [sampleCampaignId, setSampleCampaignId] = useState('');

  // New-campaign form — same targeting filters as the Leads browser.
  const [name, setName] = useState('');
  const [campStatus, setCampStatus] = useState<LeadStatus>('enrollable');
  const [segment, setSegment] = useState('');
  const [maxTier, setMaxTier] = useState('');
  const [dailyCap, setDailyCap] = useState('12');
  const [brief, setBrief] = useState('');
  // Lead facets (enrollable counts per status/segment) for the targeting dropdowns.
  const [facets, setFacets] = useState<LeadFacets | null>(null);

  const refreshCampaigns = useCallback(async () => {
    try {
      setCampaigns(await listCampaigns());
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to load campaigns');
    }
  }, [notify]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once on mount
  useEffect(() => {
    refreshCampaigns();
    loadSettings();
    getLeadFacets()
      .then(setFacets)
      .catch(() => {});
  }, []);

  const saveMaxDaily = () =>
    wrap(
      'maxDaily',
      async () => {
        const n = Number(maxDaily);
        if (!Number.isFinite(n) || n < 1) throw new Error('Enter a whole number ≥ 1');
        const s = await updateMarketingSettings(Math.floor(n));
        setMaxDaily(String(s.maxDailyCap));
      },
      'Max cold emails/day updated',
    );

  const sendTest = () =>
    wrap('testSend', async () => {
      if (!testTo.trim() || !testSubject.trim() || !testBody.trim()) {
        throw new Error('Recipient, subject, and message are all required');
      }
      const { id } = await testSend({
        to: testTo.trim(),
        subject: testSubject.trim(),
        body: testBody.trim(),
      });
      notify(
        id ? `Test email sent → ${testTo.trim()} (Resend id ${id})` : 'Test email accepted',
        'success',
      );
    });

  const loadSample = () =>
    wrap('loadSample', async () => {
      const { subject, body, campaignName } = await testSampleDraft(sampleCampaignId || undefined);
      setTestSubject(subject);
      setTestBody(body);
      notify(
        `Loaded an AI sample from “${campaignName}” — tweak if you like, then send`,
        'success',
      );
    });

  const openCampaign = (cmp: MktCampaign) => setSelected(cmp);

  const wrap = async (id: string, fn: () => Promise<void>, ok?: string) => {
    setAction(id);
    try {
      await fn();
      if (ok) notify(ok, 'success');
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setAction(null);
    }
  };

  const create = () =>
    wrap(
      'create',
      async () => {
        if (!name.trim()) throw new Error('Name is required');
        await createCampaign({
          name: name.trim(),
          segment: segment || null,
          maxTier: maxTier === '' ? null : Number(maxTier),
          leadStatus: campStatus,
          dailyCap: Number(dailyCap),
          brief: brief.trim() || null,
        });
        setName('');
        setBrief('');
        await refreshCampaigns();
      },
      'Campaign created',
    );

  const tabPill = (active: boolean) =>
    `rounded-full px-3.5 py-1.5 text-sm font-medium transition ${active ? 'bg-emerald-600 text-white shadow-sm' : 'bg-white text-neutral-600 ring-1 ring-neutral-200 hover:bg-neutral-100'}`;

  // Button label that shows a spinner + busy text while its action runs.
  const label = (id: string, idle: ReactNode, busyText: string): ReactNode =>
    action === id ? (
      <span className="inline-flex items-center gap-1.5">
        <Spin /> {busyText}
      </span>
    ) : (
      idle
    );

  // Targeting-dropdown helpers for the new-campaign form (mirror the Leads filters).
  const segLabel = (s: string) =>
    s === '(none)' ? 'Unlabeled' : s === 'generic_unknown_lob' ? 'Generic' : s;
  const fc = (k: keyof LeadFacets['counts']) =>
    facets ? ` (${facets.counts[k].toLocaleString()})` : '';
  const campStatusOptions: { value: LeadStatus; label: string }[] = [
    { value: 'enrollable', label: `Enrollable${fc('enrollable')}` },
    { value: 'sent', label: `Sent${fc('sent')}` },
    { value: 'bounced', label: `Bounced${fc('bounced')}` },
    { value: 'invalid', label: `Invalid${fc('invalid')}` },
    { value: 'risky', label: `Risky${fc('risky')}` },
    { value: 'unsubscribed', label: `Unsubscribed${fc('unsubscribed')}` },
    { value: 'ineligible', label: `Ineligible${fc('ineligible')}` },
    { value: 'all', label: 'Any status' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1.5">
          <button
            type="button"
            className={tabPill(view === 'campaigns')}
            onClick={() => setView('campaigns')}
          >
            Campaigns
          </button>
          <button
            type="button"
            className={tabPill(view === 'leads')}
            onClick={() => setView('leads')}
          >
            Leads
          </button>
          <button
            type="button"
            className={tabPill(view === 'test')}
            onClick={() => setView('test')}
          >
            Test send
          </button>
        </div>
        <p className="hidden text-xs text-neutral-500 sm:block">
          Cold outreach — AI drafts, you approve &amp; send (≤15/day). Open <b>Help</b> (top-right)
          for a walkthrough.
        </p>
      </div>

      {manageOpen && (
        <CampaignManager
          notify={notify}
          initialId={selected?.id ?? null}
          onClose={async () => {
            setManageOpen(false);
            // Reconcile the open campaign with any renames/deletes from the manager.
            const list = await listCampaigns().catch(() => campaigns);
            setCampaigns(list);
            if (selected) setSelected(list.find((c) => c.id === selected.id) ?? null);
          }}
        />
      )}

      {view === 'leads' && <LeadsBrowser notify={notify} />}

      {view === 'test' && (
        <Panel title="Send a test email">
          <p className="mb-3 text-xs text-neutral-500">
            Sends one email through the exact same pipe as a campaign (same From/Reply-To, the same
            unsubscribe headers + footer) — but not tied to any campaign and not counted against a
            daily cap. Use it to check inbox placement with{' '}
            <a
              href="https://www.mail-tester.com"
              target="_blank"
              rel="noreferrer"
              className="text-emerald-700 hover:underline"
            >
              mail-tester.com
            </a>{' '}
            or your own Gmail/Outlook.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <Labeled label="To" hint="e.g. a mail-tester.com address, or your own inbox">
              <input
                className={input}
                type="email"
                placeholder="test-xxxx@srv1.mail-tester.com"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
              />
            </Labeled>
            <Labeled label="Subject" hint="Use a realistic cold-email subject">
              <input
                className={input}
                placeholder="Quick question about your commissions"
                value={testSubject}
                onChange={(e) => setTestSubject(e.target.value)}
              />
            </Labeled>
          </div>
          <Labeled label="Message" hint="Plain text — keep it like a real outbound email">
            <textarea
              className={`${input} h-28`}
              placeholder="Hi there — …"
              value={testBody}
              onChange={(e) => setTestBody(e.target.value)}
            />
          </Labeled>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" className={btn} disabled={busy} onClick={sendTest}>
              {label('testSend', 'Send test email', 'Sending…')}
            </button>
            <span className="mx-1 text-neutral-300">·</span>
            <span className="text-xs text-neutral-500">Need content?</span>
            <select
              className={`${input} w-auto`}
              value={sampleCampaignId}
              onChange={(e) => setSampleCampaignId(e.target.value)}
              title="Which campaign's brief to draft the sample from"
            >
              <option value="">newest campaign</option>
              {campaigns.map((cmp) => (
                <option key={cmp.id} value={cmp.id}>
                  {cmp.name}
                </option>
              ))}
            </select>
            <button type="button" className={btnGhost} disabled={busy} onClick={loadSample}>
              {label('loadSample', '✨ Load a sample', 'Drafting…')}
            </button>
          </div>
        </Panel>
      )}

      {view === 'campaigns' && (
        <>
          <Panel title="Outreach settings">
            <div className="flex flex-wrap items-end gap-3">
              <Labeled
                label="Max cold emails / day"
                hint="Ceiling for each campaign's daily send cap (applies account-wide)"
              >
                <input
                  className={`${input} w-32`}
                  type="number"
                  min="1"
                  value={maxDaily}
                  placeholder="15"
                  onChange={(e) => setMaxDaily(e.target.value)}
                />
              </Labeled>
              <button type="button" className={btn} disabled={busy} onClick={saveMaxDaily}>
                {label('maxDaily', 'Save', 'Saving…')}
              </button>
            </div>
          </Panel>

          <Panel title="New campaign">
            <p className="mb-3 text-xs text-neutral-500">
              Pick who to target and how fast to send. The AI then drafts personalized emails you
              approve from the inbox.
            </p>
            <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
              <Labeled label="Name" hint="Your label for this campaign">
                <input
                  className={input}
                  placeholder="e.g. Group benefits — Tier 1"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Labeled>
              <Labeled label="Status" hint="Which lead bucket to enroll from">
                <select
                  className={input}
                  value={campStatus}
                  onChange={(e) => setCampStatus(e.target.value as LeadStatus)}
                >
                  {campStatusOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Labeled>
              <Labeled label="Segment" hint="Line of business (enrollable counts)">
                <select
                  className={input}
                  value={segment}
                  onChange={(e) => setSegment(e.target.value)}
                >
                  <option value="">Any segment</option>
                  {facets?.segments.map((s) => (
                    <option key={s.segment} value={s.segment}>
                      {segLabel(s.segment)} ({s.enrollable.toLocaleString()})
                    </option>
                  ))}
                </select>
              </Labeled>
              <Labeled label="Max tier" hint="0 = best only">
                <select
                  className={input}
                  value={maxTier}
                  onChange={(e) => setMaxTier(e.target.value)}
                >
                  <option value="">Any tier</option>
                  <option value="0">Tier 0 (best)</option>
                  <option value="1">Up to Tier 1</option>
                  <option value="2">Up to Tier 2</option>
                  <option value="3">Up to Tier 3</option>
                  <option value="4">Up to Tier 4</option>
                  <option value="5">Up to Tier 5</option>
                </select>
              </Labeled>
              <Labeled label="Daily cap" hint="Max sends/day (≤ account max)">
                <input
                  className={input}
                  placeholder="12"
                  value={dailyCap}
                  onChange={(e) => setDailyCap(e.target.value)}
                />
              </Labeled>
              <div className="flex items-end">
                <button type="button" className={`${btn} w-full`} disabled={busy} onClick={create}>
                  {label('create', 'Create', 'Creating…')}
                </button>
              </div>
            </div>
            <Labeled label="Brief (optional)" hint="The value-prop / voice the AI should lead with">
              <textarea
                className={`${input} h-16`}
                placeholder="e.g. Help Medicare agencies recover commissions carriers underpaid and catch attrition early."
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
              />
            </Labeled>
          </Panel>

          <Panel title="Campaigns">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-xs text-neutral-500">
                Open a campaign to read its inbox below — or manage everything (enroll, generate,
                auto-draft, edit, delete) full screen.
              </p>
              <button
                type="button"
                className={btn}
                onClick={() => setManageOpen(true)}
                title="Open the full-screen campaign manager"
              >
                ⤢ Open full screen
              </button>
            </div>
            {campaigns.length === 0 ? (
              <p className="text-sm text-neutral-500">No campaigns yet.</p>
            ) : (
              <ul className="space-y-1">
                {campaigns.map((cmp) => {
                  const active = selected?.id === cmp.id;
                  return (
                    <li key={cmp.id}>
                      <button
                        type="button"
                        onClick={() => openCampaign(cmp)}
                        aria-pressed={active}
                        className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition ${active ? 'border-emerald-300 bg-emerald-50' : 'border-transparent hover:border-neutral-200 hover:bg-neutral-50'}`}
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <span
                            className={`truncate font-medium ${active ? 'text-emerald-800' : 'text-neutral-800'}`}
                          >
                            {cmp.name}
                          </span>
                          <Badge tone={STATUS_TONE[cmp.lifecycle ?? cmp.status]}>
                            {cmp.lifecycle ?? cmp.status}
                          </Badge>
                          <span className="text-xs text-neutral-400">
                            {cmp.segment ?? 'any'}
                            {cmp.maxTier != null ? ` · ≤T${cmp.maxTier}` : ''} · cap {cmp.dailyCap}
                            /day
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-3">
                          <span className="flex gap-2.5 text-xs text-neutral-500">
                            {(['queued', 'drafted', 'sent', 'replied'] as const).map((s) => (
                              <span key={s}>
                                <b className="text-neutral-700">{cmp.counts?.[s] ?? 0}</b> {s}
                              </span>
                            ))}
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${active ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'}`}
                          >
                            {active ? 'Viewing' : 'Open'} <span aria-hidden="true">→</span>
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>

          {selected && (
            <Panel title={`Campaign · ${selected.name}`}>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge tone={STATUS_TONE[selected.lifecycle ?? selected.status]}>
                    {selected.lifecycle ?? selected.status}
                  </Badge>
                  <span className="text-xs text-neutral-500">
                    {selected.segment ?? 'any'}
                    {selected.maxTier != null ? ` · ≤T${selected.maxTier}` : ''} · cap{' '}
                    {selected.dailyCap}/day
                  </span>
                </div>
                <button type="button" className={btn} onClick={() => setManageOpen(true)}>
                  ⤢ Open full screen to work the inbox
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {(['queued', 'drafted', 'sent', 'replied'] as const).map((s) => (
                  <div
                    key={s}
                    className="rounded-xl border border-neutral-200 bg-white px-3 py-2.5"
                  >
                    <div className="text-xl font-semibold text-neutral-900">
                      {selected.counts?.[s] ?? 0}
                    </div>
                    <div className="text-[11px] uppercase tracking-wide text-neutral-500">{s}</div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-neutral-500">
                Enroll leads, generate drafts, and work the threaded inbox in the full-screen
                manager.
              </p>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}

/** Small inline spinner using the current text color. */
function Spin() {
  return (
    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70" />
  );
}

/** A labeled field with a one-line hint — used in the New-campaign form. */
function Labeled({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the form control is passed in via children
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-neutral-600">{label}</span>
      {children}
      {hint && (
        <span className="mt-0.5 block text-[11px] leading-tight text-neutral-400">{hint}</span>
      )}
    </label>
  );
}

const tierTone = (t: number): Tone =>
  t === 0 ? 'emerald' : t === 1 ? 'sky' : t === 2 ? 'amber' : 'neutral';

/** Human label for a suppression reason (do-not-contact). */
const suppressLabel = (reason?: string | null): string =>
  reason === 'hard_bounce'
    ? 'Bounced'
    : reason === 'spam_complaint'
      ? 'Complaint'
      : reason === 'reply_opt_out' ||
          reason === 'one_click_unsubscribe' ||
          reason === 'unsubscribe_link'
        ? 'Unsubscribed'
        : 'Suppressed';

/** Browse the imported ICP leads — filter by deliverability/engagement status. */
function LeadsBrowser({ notify }: { notify: Notify }) {
  const [status, setStatus] = useState<LeadStatus>('enrollable');
  const [segment, setSegment] = useState('');
  const [maxTier, setMaxTier] = useState('');
  const [q, setQ] = useState('');
  const [facets, setFacets] = useState<LeadFacets | null>(null);
  const [leads, setLeads] = useState<MktLead[]>([]);
  const [loading, setLoading] = useState(false);
  // Which row is expanded (accordion) + its lazily-loaded detail.
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MktLeadDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const toggleLead = async (id: string) => {
    if (openId === id) {
      setOpenId(null);
      setDetail(null);
      return;
    }
    setOpenId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await getLead(id));
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to load lead');
      setOpenId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setLeads(
        await listLeads({
          status,
          segment: segment || undefined,
          maxTier: maxTier === '' ? undefined : Number(maxTier),
          q: q.trim() || undefined,
          limit: 100,
        }),
      );
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [status, segment, maxTier, q, notify]);

  const loadFacets = useCallback(async () => {
    try {
      setFacets(await getLeadFacets());
    } catch {
      /* non-fatal — counts just won't show */
    }
  }, []);

  // --- Manually add a lead (warm inbound), optionally inviting them to register.
  const emptyNew = {
    email: '',
    firstName: '',
    lastName: '',
    companyName: '',
    title: '',
    phone: '',
    segment: '',
    tier: '0',
  };
  const [showNew, setShowNew] = useState(false);
  const [nl, setNl] = useState(emptyNew);
  const [creating, setCreating] = useState(false);
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const setNlField = (k: keyof typeof emptyNew, v: string) => setNl((s) => ({ ...s, [k]: v }));

  const submitNew = async (invite: boolean) => {
    if (!nl.email.trim()) {
      notify('An email is required');
      return;
    }
    setCreating(true);
    try {
      const r = await createLead({
        email: nl.email.trim(),
        firstName: nl.firstName.trim() || undefined,
        lastName: nl.lastName.trim() || undefined,
        companyName: nl.companyName.trim() || undefined,
        title: nl.title.trim() || undefined,
        phone: nl.phone.trim() || undefined,
        segment: nl.segment.trim() || undefined,
        tier: nl.tier === '' ? undefined : Number(nl.tier),
        invite,
      });
      const base = r.existing ? 'Lead already existed' : 'Lead created';
      const inv = invite
        ? r.invited
          ? ' · invite sent'
          : ` · invite NOT sent${r.inviteError ? ` (${r.inviteError})` : ''}`
        : '';
      notify(base + inv);
      setNl(emptyNew);
      setShowNew(false);
      load();
      loadFacets();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to create lead');
    } finally {
      setCreating(false);
    }
  };

  const inviteOne = async (id: string) => {
    setInvitingId(id);
    try {
      await inviteLead(id);
      notify('Invite sent');
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Invite failed');
    } finally {
      setInvitingId(null);
    }
  };

  // Auto-reload when the dropdown filters change; the text search applies on submit.
  // Facets (the per-status counts) load once on mount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: q is applied via the Search form, not per keystroke
  useEffect(() => {
    load();
  }, [status, segment, maxTier]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once on mount
  useEffect(() => {
    loadFacets();
  }, []);

  const n = (k: keyof LeadFacets['counts']) =>
    facets ? ` (${facets.counts[k].toLocaleString()})` : '';
  const statusOptions: { value: LeadStatus; label: string }[] = [
    { value: 'enrollable', label: `Enrollable${n('enrollable')}` },
    { value: 'sent', label: `Sent${n('sent')}` },
    { value: 'bounced', label: `Bounced${n('bounced')}` },
    { value: 'invalid', label: `Invalid${n('invalid')}` },
    { value: 'risky', label: `Risky${n('risky')}` },
    { value: 'unsubscribed', label: `Unsubscribed${n('unsubscribed')}` },
    { value: 'ineligible', label: `Ineligible${n('ineligible')}` },
    { value: 'all', label: `All${facets ? ` (${facets.total.toLocaleString()})` : ''}` },
  ];
  const segLabel = (s: string) =>
    s === '(none)' ? 'Unlabeled' : s === 'generic_unknown_lob' ? 'Generic' : s;

  const cell = 'px-2 py-1.5 align-top';
  return (
    <Panel title="Leads — your ICP targets">
      <p className="mb-3 text-xs text-neutral-500">
        Filter by <b>Status</b> (counts shown) — <b>Enrollable</b> = verified-deliverable &amp; not
        yet sent; the rest are sent, bounced, or suppressed by reason. <b>Tier</b> = quality (0–2 =
        ICP-enriched, 3–5 = verified-but-unenriched). Click <b>View</b> on a lead for its full
        history.
      </p>

      {/* Manually add a warm lead (e.g. handed over from a post) + optionally invite. */}
      <div className="mb-4 rounded-xl border border-neutral-200 bg-neutral-50/60 p-3">
        <button
          type="button"
          className="text-sm font-medium text-emerald-700 hover:underline"
          aria-expanded={showNew}
          onClick={() => setShowNew((v) => !v)}
        >
          {showNew ? '− Hide' : '＋ Add a lead'}
        </button>
        {showNew && (
          <form
            className="mt-3"
            onSubmit={(e) => {
              e.preventDefault();
              submitNew(false);
            }}
          >
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Labeled label="Email *">
                <input
                  className={input}
                  type="email"
                  placeholder="agent@agency.com"
                  value={nl.email}
                  onChange={(e) => setNlField('email', e.target.value)}
                />
              </Labeled>
              <Labeled label="First name">
                <input
                  className={input}
                  value={nl.firstName}
                  onChange={(e) => setNlField('firstName', e.target.value)}
                />
              </Labeled>
              <Labeled label="Last name">
                <input
                  className={input}
                  value={nl.lastName}
                  onChange={(e) => setNlField('lastName', e.target.value)}
                />
              </Labeled>
              <Labeled label="Company">
                <input
                  className={input}
                  value={nl.companyName}
                  onChange={(e) => setNlField('companyName', e.target.value)}
                />
              </Labeled>
              <Labeled label="Title">
                <input
                  className={input}
                  value={nl.title}
                  onChange={(e) => setNlField('title', e.target.value)}
                />
              </Labeled>
              <Labeled label="Phone">
                <input
                  className={input}
                  value={nl.phone}
                  onChange={(e) => setNlField('phone', e.target.value)}
                />
              </Labeled>
              <Labeled label="Segment" hint="Defaults to 'inbound'">
                <input
                  className={input}
                  placeholder="inbound"
                  value={nl.segment}
                  onChange={(e) => setNlField('segment', e.target.value)}
                />
              </Labeled>
              <Labeled label="Tier">
                <select
                  className={input}
                  value={nl.tier}
                  onChange={(e) => setNlField('tier', e.target.value)}
                >
                  {[0, 1, 2, 3, 4, 5].map((t) => (
                    <option key={t} value={t}>
                      Tier {t}
                      {t === 0 ? ' (best)' : ''}
                    </option>
                  ))}
                </select>
              </Labeled>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button type="submit" className={btnGhost} disabled={creating}>
                {creating ? 'Saving…' : 'Add lead'}
              </button>
              <button
                type="button"
                className={btn}
                disabled={creating}
                onClick={() => submitNew(true)}
              >
                {creating ? 'Saving…' : 'Add & invite to register'}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Labeled label="Status">
          <select
            className={input}
            value={status}
            onChange={(e) => setStatus(e.target.value as LeadStatus)}
          >
            {statusOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Labeled>
        <Labeled label="Segment" hint="Enrollable counts">
          <select className={input} value={segment} onChange={(e) => setSegment(e.target.value)}>
            <option value="">Any segment</option>
            {facets?.segments.map((s) => (
              <option key={s.segment} value={s.segment}>
                {segLabel(s.segment)} ({s.enrollable.toLocaleString()})
              </option>
            ))}
          </select>
        </Labeled>
        <Labeled label="Tier">
          <select className={input} value={maxTier} onChange={(e) => setMaxTier(e.target.value)}>
            <option value="">Any tier</option>
            <option value="0">Tier 0 (best)</option>
            <option value="1">Up to Tier 1</option>
            <option value="2">Up to Tier 2</option>
            <option value="3">Up to Tier 3</option>
            <option value="4">Up to Tier 4</option>
            <option value="5">Up to Tier 5</option>
          </select>
        </Labeled>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
        >
          <Labeled label="Search">
            <input
              className={`${input} w-64`}
              placeholder="Company, name, email, domain…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </Labeled>
          <button type="submit" className={btn} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>
        </form>
        <span className="text-xs text-neutral-400">{leads.length} shown (max 100, best first)</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full table-auto text-sm">
          <thead className="border-b border-neutral-200 text-left text-xs uppercase tracking-wide text-neutral-500">
            <tr>
              <th className={cell}>Company</th>
              <th className={cell}>Contact</th>
              <th className={cell}>Tier</th>
              <th className={cell}>Score</th>
              <th className={cell}>Segment</th>
              <th className={cell}>Location</th>
              <th className={cell}>Domain</th>
              <th className={`${cell} text-right`}>Action</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => {
              const open = openId === l.id;
              return (
                <Fragment key={l.id}>
                  <tr
                    className={`border-t border-neutral-100 ${open ? 'bg-emerald-50/50' : 'hover:bg-neutral-50'}`}
                  >
                    <td className={`${cell} font-medium`}>
                      {l.companyName ?? '—'}
                      {l.suppressed && (
                        <span className="mt-0.5 block">
                          <Badge tone="rose">{suppressLabel(l.suppressedReason)}</Badge>
                        </span>
                      )}
                    </td>
                    <td className={cell}>
                      {[l.firstName, l.lastName].filter(Boolean).join(' ') || '—'}
                      {l.title ? (
                        <span className="block text-xs text-neutral-400">{l.title}</span>
                      ) : null}
                    </td>
                    <td className={cell}>
                      <Badge tone={tierTone(l.tier)}>T{l.tier}</Badge>
                    </td>
                    <td className={cell}>{l.icpScore ?? '—'}</td>
                    <td className={`${cell} text-neutral-500`}>{l.segment ?? '—'}</td>
                    <td className={`${cell} text-neutral-500`}>
                      {[l.city, l.state].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className={`${cell} font-mono text-xs text-neutral-500`}>
                      {l.domain ?? '—'}
                    </td>
                    <td className={`${cell} text-right`}>
                      <div className="flex items-center justify-end gap-3">
                        {l.email && !l.suppressed && (
                          <button
                            type="button"
                            className="text-xs font-medium text-emerald-600 hover:underline disabled:opacity-50"
                            disabled={invitingId === l.id}
                            onClick={() => inviteOne(l.id)}
                            title="Email a personal invite to register"
                          >
                            {invitingId === l.id ? 'Inviting…' : 'Invite'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="text-xs font-medium text-emerald-600 hover:underline"
                          aria-expanded={open}
                          onClick={() => toggleLead(l.id)}
                        >
                          {open ? 'Hide ▲' : 'View →'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-emerald-50/20">
                      <td colSpan={8} className="px-2 pb-3">
                        {detailLoading || !detail ? (
                          <p className="py-2 text-sm text-neutral-400">Loading…</p>
                        ) : (
                          <LeadDetail
                            detail={detail}
                            onClose={() => {
                              setOpenId(null);
                              setDetail(null);
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {leads.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="px-2 py-3 text-sm text-neutral-400">
                  No leads match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/** Full status of one lead: facts, campaigns it's in, and its message timeline. */
function LeadDetail({ detail, onClose }: { detail: MktLeadDetail; onClose: () => void }) {
  const { lead, enrollments, timeline, suppressed, suppressedReason, sentCount, replyCount } =
    detail;
  const name = [lead.firstName, lead.lastName].filter(Boolean).join(' ') || '—';
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold">{lead.companyName ?? lead.email}</h4>
            <Badge tone={tierTone(lead.tier)}>T{lead.tier}</Badge>
            {suppressed && <Badge tone="rose">{suppressLabel(suppressedReason)}</Badge>}
          </div>
          <p className="text-xs text-neutral-500">
            {name}
            {lead.title ? ` · ${lead.title}` : ''} · {lead.email ?? 'no email'} ·{' '}
            {lead.segment ?? 'n/a'} · score {lead.icpScore ?? '—'} ·{' '}
            {[lead.city, lead.state].filter(Boolean).join(', ') || 'n/a'}
            {lead.websiteCheckedUrl ? (
              <>
                {' · '}
                <a
                  href={lead.websiteCheckedUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-emerald-600 hover:underline"
                >
                  site
                </a>
              </>
            ) : null}
          </p>
          <p className="mt-1 text-xs text-neutral-500">
            <b>{enrollments.length}</b> campaign(s) · <b>{sentCount}</b> sent · <b>{replyCount}</b>{' '}
            repl{replyCount === 1 ? 'y' : 'ies'}
          </p>
        </div>
        <button type="button" className={btnGhost} onClick={onClose}>
          Close
        </button>
      </div>

      <div className="mt-3">
        <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
          Campaigns
        </div>
        {enrollments.length === 0 ? (
          <p className="text-sm text-neutral-400">Not enrolled in any campaign.</p>
        ) : (
          <ul className="mt-1 space-y-1">
            {enrollments.map((e) => (
              <li key={e.id} className="flex items-center gap-2 text-sm">
                <Badge tone={STATUS_TONE[e.status] ?? 'neutral'}>{e.status}</Badge>
                <span>{e.campaignName}</span>
                <span className="text-xs text-neutral-400">enrolled {fmtWhen(e.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-3">
        <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
          Message timeline ({timeline.length})
        </div>
        {timeline.length === 0 ? (
          <p className="text-sm text-neutral-400">No messages yet.</p>
        ) : (
          <ul className="mt-1 space-y-2">
            {timeline.map((m) => (
              <li
                key={m.id}
                className={`rounded-lg border p-2.5 text-sm ${m.direction === 'inbound' ? 'border-emerald-200 bg-emerald-50/50' : 'border-neutral-200 bg-white'}`}
              >
                <div className="flex items-center justify-between text-xs text-neutral-500">
                  <span>
                    {m.direction === 'inbound' ? '↩ Reply' : m.aiGenerated ? '🤖 AI' : 'Outbound'} ·{' '}
                    {m.status} · {m.campaignName ?? 'campaign'}
                  </span>
                  <span>{fmtWhen(m.sentAt ?? m.createdAt)}</span>
                </div>
                {m.subject && <div className="font-medium">{m.subject}</div>}
                <div className="whitespace-pre-wrap text-neutral-700">{m.body}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
